import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { token, type } from '../theme/tokens.js';
import { Eyebrow, type EyebrowTone } from './Eyebrow.js';

export interface MetaRowProps {
  /**
   * Timestamp. Rendered in amber — the design system reserves amber for time,
   * so it reads as objective metadata rather than emphasis.
   */
  time?: string;
  /** Machine-readable timestamp for the `<time>` element. ISO 8601. */
  dateTime?: string;
  /** Uppercase type label, e.g. `UPLOADED` or `SEVERED`. */
  label?: ReactNode;
  labelTone?: EyebrowTone;
  /** Content pinned to the trailing edge. */
  trailing?: ReactNode;
  /**
   * The timestamp's size. `xs` is the metadata row above card content, which is
   * what this component was built for and stays the default.
   *
   * `sm` exists for Files' tiles, where the date is one of only two type steps
   * on the card and has to match the body lines it sits under — the colour is
   * what marks it as time there, not the size. Deliberately two named steps
   * rather than an open `fontSize`: a caller free to pass anything is a caller
   * free to add a third step, which is the thing the tiles were fixing.
   */
  size?: 'xs' | 'sm';
  className?: string;
}

/**
 * The metadata row that sits above card content.
 *
 * `design/design-system/readme.md`: *"Cards carry a top metadata row (amber
 * time + uppercase type label) above the content"* and *"Time is rendered in
 * amber to read as objective metadata."*
 *
 * Using this component is how a card gets that treatment right by default —
 * the amber-for-time rule is encoded here rather than left to each caller.
 */
export function MetaRow({
  time,
  dateTime,
  label,
  labelTone = 'muted',
  trailing,
  size = 'xs',
  className,
}: MetaRowProps) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      {time ? (
        <time
          {...(dateTime !== undefined ? { dateTime } : {})}
          style={{
            color: token('bp-amber'),
            fontSize: type.size[size],
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {time}
        </time>
      ) : null}

      {label ? <Eyebrow tone={labelTone}>{label}</Eyebrow> : null}

      {trailing ? <div className="ml-auto flex items-center gap-2">{trailing}</div> : null}
    </div>
  );
}
